import React, {
  Fragment,
  useState,
  useEffect,
  useReducer,
  useCallback,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useSearch } from "../../../searchServices";
import { useDebounce } from "../../../../util/hooks";
import { isBlank } from "../../../../util/ext";
import { equals, append, mergeRight, propEq, reject, any } from "ramda";

import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import Typography from "@mui/material/Typography";

import PanelLoadingIndicator from "../../../../components/PanelLoadingIndicator";
import AddMediaToParentMenuItem from "../../../common/AddMediaToParentMenuItem";

const useStyles = makeStyles(theme => ({
  search: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: "4px",
    position: "relative",
    width: "95%",
    display: "flex",
    alignItems: "center",
    margin: "auto",
  },

  searchIcon: {
    color: theme.palette.primary.darker,
  },

  inputRoot: {
    color: theme.palette.primary.darker,
    flexGrow: 1,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
  },

  inputFocused: {
    borderColor: theme.palette.primary.brandPrimary,
  },

  outline: {
    borderColor: "transparent",
  },

  inputInput: {
    width: "100%",
    padding: theme.spacing(2.5),
  },

  mediaContainer: {
    height: "275px",
    overflowY: "auto",
    marginTop: theme.spacing(2.5),
  },

  menuItem: {
    height: 55,
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.primary.brandPrimary,
    },
  },

  footer: {
    padding: theme.spacing(1.5, 2, 0, 2),
  },

  selectedCount: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
  },

  moreButtonContainer: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  moreButton: {
    width: "100%",
  },
}));

const initialSearchState = {
  appliedQuery: [],
  appliedFacets: [],
  orderBy: "name",
  orderDirection: "asc",
  page: 0,
  titleOnly: true,
  moduleOnly: false,
  limit: 20,
  otherParams: [],
  noFacets: true,
};

const AddToShowcaseMenuItems = props => {
  const classes = useStyles();
  const { open, moduleId, showcaseItems, onAddClicked } = props;

  const [mediaList, setMediaList] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(showcaseItems);

  const handleRowClick = useCallback(
    (event, item) => {
      const updatedMediaList = any(propEq("id", item.id), selectedMedia)
        ? reject(propEq("id", item.id), selectedMedia)
        : append(item, selectedMedia);
      setSelectedMedia(updatedMediaList);
    },
    [selectedMedia],
  );

  const handleAddMedia = useCallback(() => {
    onAddClicked(selectedMedia);
  }, [onAddClicked, selectedMedia]);

  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case "UPDATE_PAGE":
        return { ...state, page: action.page };
      case "UPDATE_QUERY":
        return {
          ...state,
          appliedQuery: [action.query],
          page: initialSearchState.page,
        };
      default:
        return state;
    }
  }, initialSearchState);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const onSearchChange = useCallback(event => {
    setSearchTerm(event.target.value);
  }, []);

  useEffect(() => {
    setMediaList([]);
    dispatch({ type: "UPDATE_QUERY", query: debouncedSearchTerm });
  }, [selectedMedia, debouncedSearchTerm]);

  const searchResults = useSearch(
    mergeRight(state, {
      appliedFacets: [
        {
          name: "modules",
          queryParam: "_mod",
          terms: [
            {
              code: moduleId,
            },
          ],
        },
      ],
    }),
  );

  useEffect(() => {
    if (searchResults.isSuccess) {
      setMediaList(current =>
        equals(state.page, 0)
          ? searchResults.data.media
          : current.concat(searchResults.data.media),
      );
    }
  }, [state, searchResults.data, searchResults.isSuccess]);

  //-- Reset parent list on close
  useEffect(() => {
    if (!open) {
      setMediaList([]);
    }
  }, [open]);

  //-- Handle loading styling
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (searchResults.isSuccess) {
      setLoading(false);
    }
  }, [searchResults.isSuccess]);

  return (
    <Container
      disableGutters
      style={{ height: searchResults.isSuccess ? "100%" : "300px" }}
    >
      <div className={classes.search}>
        <OutlinedInput
          autoFocus
          placeholder="Search…"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
            notchedOutline: classes.outline,
            focused: classes.inputFocused,
          }}
          onChange={onSearchChange}
          inputProps={{ "aria-label": "search" }}
          value={searchTerm}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon
                fontSize="large"
                classes={{ root: classes.searchIcon }}
              />
            </InputAdornment>
          }
        />
      </div>
      {!loading ? (
        <Fragment>
          <Container disableGutters classes={{ root: classes.mediaContainer }}>
            {mediaList.map(media => (
              <AddMediaToParentMenuItem
                key={media.id}
                parent={media}
                checked={any(propEq("id", media.id), selectedMedia)}
                aria-checked={any(propEq("id", media.id), selectedMedia)}
                onClick={event => handleRowClick(event, media)}
              />
            ))}
            <Fragment>
              {searchResults.isSuccess ? (
                searchResults.data.hasMore && (
                  <Fragment>
                    <MenuItem classes={{ root: classes.moreButtonContainer }}>
                      <Button
                        variant="text"
                        onClick={() =>
                          dispatch({
                            type: "UPDATE_PAGE",
                            page: state.page + 1,
                          })
                        }
                        classes={{ root: classes.moreButton }}
                      >
                        More...
                      </Button>
                    </MenuItem>
                  </Fragment>
                )
              ) : (
                <div>
                  <PanelLoadingIndicator />
                </div>
              )}
            </Fragment>
          </Container>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            className={classes.footer}
          >
            <Typography variant="h5" className={classes.selectedCount}>
              {selectedMedia.length} selected
            </Typography>
            <Button
              disabled={isBlank(selectedMedia)}
              startIcon={<AddIcon />}
              onClick={handleAddMedia}
            >
              Add
            </Button>
          </Grid>
        </Fragment>
      ) : (
        <PanelLoadingIndicator />
      )}
    </Container>
  );
};

export default AddToShowcaseMenuItems;
